import React, { useContext, useEffect, useState } from "react";
import { FiPlus, FiUploadCloud, FiSave, FiX, FiImage } from "react-icons/fi";
import { toast } from "react-hot-toast";

import useClients from "../../../Services/ClientServices/ClientConfig/useClientConfig";
import { GlobalContext } from "../../../../Context/GlobalContext";
import { uploadItemImage } from "../../../../Utils/imageUploadUtils";

/* ---------------- CONSTANTS ---------------- */
const THEME_BLUE_BG = "bg-[#F8FBFF]";
const BORDER_SMART = "border-gray-300";

const DEFAULT_BANNER = {
  banner_id: "",
  banner_url: "",
  background_url: "",
};

function BannerSection({ clientId }) {
  const { anlookApi } = useContext(GlobalContext);
  const { getBanner, updateBanner } = useClients();

  const [banner, setBanner] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [form, setForm] = useState(DEFAULT_BANNER);
  const [saving, setSaving] = useState(false);

  const fetchBanner = async () => {
    setLoading(true);
    const data = await getBanner(clientId);
    setBanner(data);
    setLoading(false);
  };

  useEffect(() => {
    if (clientId) fetchBanner();
  }, [clientId]);

  /* ---------- OPEN MODAL ---------- */
  const openBannerModal = () => {
    if (!banner) {
      setForm(DEFAULT_BANNER);
    } else {
      setForm({
        banner_id: banner.banner_id || "",
        banner_url: banner.banner_url || "",
        background_url: banner.background_url || "",
      });
    }
    setOpenModal(true);
  };

  const closeModal = () => {
    setOpenModal(false);
    setForm(DEFAULT_BANNER);
  };

  /* ---------- IMAGE UPLOAD ---------- */
 

const uploadImage = async (file, field) => {
  if (!file) return;

  if (!clientId) {
    toast.error("Client id missing");
    return;
  }

  const toastId = toast.loading("Uploading image...");

  try {
    const result = await uploadItemImage({
      anlookApi,
      userid: clientId,
      itemName: field === "background_url" ? "banner_background" : "banner",
      file,
      maxSizeMB: 5,
    });

    const imageUrl = result?.imageUrl;

    if (!imageUrl) {
      throw new Error("Image URL missing");
    }

    setForm((previousForm) => ({
      ...previousForm,
      [field]: imageUrl,
    }));

    toast.success("Image uploaded", {
      id: toastId,
    });
  } catch (error) {
    console.error("Banner image upload error:", error);

    toast.error(error?.message || "Upload failed", {
      id: toastId,
    });
  }
};

  /* SAVE BANNER  */
  const handleSave = async () => {
    if (!form.banner_url) {
      toast.error("Banner image is required");
      return;
    }

    setSaving(true);

    const success = await updateBanner({
      userid: clientId,
      banner_id: form.banner_id,
      banner_url: form.banner_url,
      background_url: form.background_url,
    });

    if (success) {
      closeModal();
      fetchBanner();
    }

    setSaving(false);
  };

  return (
    <div
      className={`rounded-2xl bg-white p-7 shadow-sm border ${BORDER_SMART} mt-8`}
    >
      {/* HEADER */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <div className="text-[11px] font-extrabold uppercase tracking-[0.15em] text-gray-400">
            Banner Management
          </div>
          <p className="text-xs text-gray-500 font-semibold mt-1">
            Manage homepage banner and background visuals
          </p>
        </div>

        <button
          onClick={openBannerModal}
          className="flex items-center gap-2 bg-[#01C37D] text-white px-5 py-2.5 rounded-xl text-xs font-bold hover:bg-[#00ab6d] transition-all shadow-lg shadow-[#01C37D]/20"
        >
          <FiPlus /> {banner ? "Edit Banner" : "Add Banner"}
        </button>
      </div>

      {/* CONTENT */}
      {loading ? (
        <div className="py-14 flex justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#01C37D]/20 border-t-[#01C37D]" />
        </div>
      ) : !banner ? (
        <div className="py-14 border border-dashed border-gray-300 rounded-2xl text-center">
          <p className="text-gray-400 text-sm mb-4">
            No banner configured for this client.
          </p>
          <button
            onClick={openBannerModal}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#01C37D] text-white text-xs font-bold uppercase tracking-widest shadow hover:bg-[#00ab6d]"
          >
            <FiPlus /> Add Banner
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
          {/* SMALL SQUARE BANNER */}
          <div className="rounded-xl border overflow-hidden w-1/2">
            <div className="aspect-square">
              <img
                src={banner.banner_url}
                alt="Banner"
                className="w-full h-full object-cover"
              />
            </div>
            <p className="text-xs text-center py-2 font-bold text-gray-400">
              Banner Image (Square)
            </p>
          </div>

          {/* BACKGROUND IMAGE */}
          {banner.background_url && (
            <div className="rounded-xl border overflow-hidden">
              <img
                src={banner.background_url}
                alt="Background"
                className="w-full h-40 object-cover"
              />
              <p className="text-xs text-center py-2 font-bold text-gray-400">
                Background Image
              </p>
            </div>
          )}
        </div>
      )}

      {/* MODAL */}
      {openModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl w-full max-w-xl shadow-2xl overflow-hidden">
            {/* MODAL HEADER */}
            <div className="px-6 py-4 border-b flex justify-between items-center bg-[#F8FBFF]">
              <h3 className="text-lg font-bold">
                {form.banner_id ? "Edit Banner" : "Add Banner"}
              </h3>
              <button onClick={closeModal}>
                <FiX size={20} />
              </button>
            </div>

            {/* MODAL BODY */}
            <div className="p-6 space-y-6">
              {/* SMALL SQUARE BANNER UPLOAD */}
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2 block">
                  Banner Image (Square)
                </label>
                <label
                  className={`flex aspect-square w-1/2 items-center justify-center border ${BORDER_SMART} rounded-xl cursor-pointer ${THEME_BLUE_BG}`}
                >
                  {form.banner_url ? (
                    <img
                      src={form.banner_url}
                      className="h-full w-full object-cover rounded-xl"
                    />
                  ) : (
                    <FiUploadCloud size={24} className="text-blue-400" />
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(event) =>
                      uploadImage(event.target.files?.[0], "banner_url")
                    }
                  />
                </label>
              </div>

              {/* BACKGROUND UPLOAD */}
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2 block">
                  Background Image (Optional)
                </label>
                <label
                  className={`flex h-32 items-center justify-center border ${BORDER_SMART} rounded-xl cursor-pointer ${THEME_BLUE_BG}`}
                >
                  {form.background_url ? (
                    <img
                      src={form.background_url}
                      className="h-full w-full object-cover rounded-xl"
                    />
                  ) : (
                    <FiImage size={22} className="text-gray-400" />
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(event) =>
                      uploadImage(event.target.files?.[0], "background_url")
                    }
                  />
                </label>
              </div>
            </div>

            {/* MODAL FOOTER */}
            <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3">
              <button
                onClick={closeModal}
                className="px-5 py-2 text-xs font-bold uppercase text-gray-500"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-6 py-2 rounded-xl bg-[#01C37D] text-white text-xs font-bold uppercase disabled:opacity-50"
              >
                <FiSave /> {saving ? "Saving..." : "Save Banner"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BannerSection;
